import {
  Button,
  Card,
  Collapse,
  Descriptions,
  Drawer,
  Modal,
  message,
  Spin,
  Table,
  FormInstance,
} from "antd";
import React, { Fragment, RefObject } from "react";
import { NavLink, RouteComponentProps } from "react-router-dom";
import EntitledSection from "../components/EntitledSection";
import {
  BellOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import entitlements from "../config/entitlements";
import {
  APIError,
  NotificationRecord,
  Trustee,
  TrusteeContactPerson,
  TrusteeScheme,
  TrusteeCompanySubscription,
} from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import { trusteeVanLabelFor } from "../helpers/constants";
import { AxiosResponse } from "axios";
import {
  CREATE_TRUSTEE_CONTACT_PERSON,
  CREATE_TRUSTEE_SCHEME,
  DELETE_TRUSTEE,
  DELETE_TRUSTEE_CONTACT_PERSON,
  GET_TRUSTEE,
  GET_TRUSTEE_NOTIFICATIONS,
  UPDATE_TRUSTEE_CONTACT_PERSON,
} from "../gql/trustees";
import ModalCreator from "../components/ModalCreator";
import * as Sentry from "@sentry/browser";
import { NewEntityUI } from "./Roles";
import {
  notificationColumns,
  NotificationRecordTableFactory,
} from "./NotificationRecords";
import BaseForm from "../components/Form/BaseForm";

interface RouteParams {
  id: string;
}

interface State {
  name: string;
  isLoading: boolean;
  id: string | number;
  showDialog: boolean;
  trustee?: Trustee;
  trusteeSchemes?: TrusteeScheme[];
  showNewSchemeModal: boolean;
  isCreatingScheme: boolean;
  notifierRecords: NotificationRecord[];
  loadedNotifierRecords: boolean;
  isLoadingNotifications: boolean;
  showNotificationsDrawer: boolean;
  contactPerson?: TrusteeContactPerson;
  showContactModal: boolean;
  isSavingContact: boolean;
}

export const getSchemeColumns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render(text: string, record: TrusteeScheme): JSX.Element {
      return (
        <span>
          <NavLink to={`/trustees/${record.trustee.id}/schemes/${record.id}`}>
            {record.name}
          </NavLink>
        </span>
      );
    },
  },
];

function ModalWrapper(
  showDialog: boolean,
  deleteTrustee: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteTrustee}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this trustee?</div>
    </Modal>
  );
}

export default class ViewTrustee extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  contactReference: RefObject<FormInstance>;

  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      name: "",
      isLoading: true,
      id: "",
      showDialog: false,
      trustee: undefined,
      trusteeSchemes: [],
      showNewSchemeModal: false,
      isCreatingScheme: false,
      notifierRecords: [],
      loadedNotifierRecords: false,
      isLoadingNotifications: false,
      showNotificationsDrawer: false,
      showContactModal: false,
      isSavingContact: false,
    };
    this.reference = React.createRef<FormInstance>();
    this.contactReference = React.createRef<FormInstance>();
    this.deleteTrustee = this.deleteTrustee.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.redirectToAllTrusteesPage = this.redirectToAllTrusteesPage.bind(this);
    this.toggleCreateSchemeModal = this.toggleCreateSchemeModal.bind(this);
    this.createTrusteeScheme = this.createTrusteeScheme.bind(this);
    this.redirectToViewSchemePage = this.redirectToViewSchemePage.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getTrustee();
    });
  }

  getTrustee = async (): Promise<void> => {
    const { id } = this.state;

    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<any> = await request({
        query: GET_TRUSTEE,
        variables: { id },
      });

      this.setState({ isLoading: false });

      if (data?.errors) {
        message.error("Failed to fetch trustee data due to server errors.");
        return;
      }

      if (data?.data?.trustee) {
        this.setState({
          trustee: data.data.trustee,
          name: data.data.trustee.name,
          trusteeSchemes: data.data.trustee.trusteeSchemes,
          contactPerson: data.data.trustee.trusteeContactPerson,
        });
      } else {
        message.error("Trustee data not found.");
      }
    } catch (error) {
      message.error(
        `An error occurred while fetching trustee data. - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
      this.setState({ isLoading: false });
    }
  };

  deleteTrustee(): void {
    const { id } = this.state;

    this.setState({ isLoading: true });

    request({
      query: DELETE_TRUSTEE,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<any>): void => {
        this.setState({ isLoading: false, showDialog: false });
        if (!data.errors && data.data.deleteTrustee.result) {
          message.success("Trustee deleted successfuly");
          this.redirectToAllTrusteesPage();
        } else {
          message.error("Failed to delete trustee");
        }
      })
      .catch((): void => {
        this.setState({ isLoading: false, showDialog: false });
      });
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllTrusteesPage(): void {
    this.props.history.push("/trustees");
  }

  redirectToViewSchemePage = (schemeId: string): void => {
    this.props.history.push(`/trustees/${this.state.id}/schemes/${schemeId}`);
  };

  openNotifications = (): void => {
    this.setState({ showNotificationsDrawer: true });
    if (this.state.loadedNotifierRecords) return;

    this.setState({ isLoadingNotifications: true });
    request({
      query: GET_TRUSTEE_NOTIFICATIONS,
      variables: { id: this.state.id },
    })
      .then(({ data }: AxiosResponse<{ data: { trustee: Trustee } }>): void => {
        this.setState({
          notifierRecords: data.data.trustee?.notifierRecords ?? [],
          loadedNotifierRecords: true,
        });
      })
      .catch((error) => {
        message.error("Could Not Fetch Notifications");
        Sentry.captureException(error);
      })
      .finally((): void => {
        this.setState({ isLoadingNotifications: false });
      });
  };

  closeNotifications = (): void => {
    this.setState({ showNotificationsDrawer: false });
  };

  toggleCreateSchemeModal(): void {
    this.setState((prevState) => ({
      showNewSchemeModal: !prevState.showNewSchemeModal,
    }));
  }

  async createTrusteeScheme(): Promise<void> {
    interface Response {
      trusteeScheme: {
        id: string;
        name: string;
        errors: APIError[];
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(["name"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      this.setState({ isCreatingScheme: true });
      const response: AxiosResponse<Response> = await request({
        query: CREATE_TRUSTEE_SCHEME,
        variables: {
          name: data.name,
          trusteeId: this.state.id,
        },
      });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createTrusteeScheme",
      );

      this.setState({ isCreatingScheme: false });

      if (mutation?.trusteeScheme) {
        message.success("Trustee scheme created successfully");
        this.redirectToViewSchemePage(mutation?.trusteeScheme.id);
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.error(
          `An Error Occurred. Your request couldn't be processed. - ${generateDisplayErrorMessage(
            err,
          )}`,
        );
        return;
      }
    } catch (error) {
      this.setState({ isLoading: false });
      message.error(
        `Failed to create trustee scheme - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
      Sentry.captureException(error);
    }
  }

  openContactModal = (): void => {
    this.setState({ showContactModal: true }, () => {
      const contact = this.state.contactPerson;
      this.contactReference.current?.setFieldsValue({
        firstName: contact?.firstName || "",
        middleName: contact?.middleName || "",
        lastName: contact?.lastName || "",
        email: contact?.email || "",
        phoneNumber: contact?.phoneNumber || "",
        emailsInCopy: contact?.emailsInCopy?.length
          ? contact.emailsInCopy
          : [""],
      });
    });
  };

  toggleContactModal = (): void => {
    this.setState((prev) => ({ showContactModal: !prev.showContactModal }));
  };

  saveContactPerson = async (): Promise<void> => {
    if (!this.contactReference.current) return;

    let data;
    try {
      data = await this.contactReference.current.validateFields([
        "firstName",
        "middleName",
        "lastName",
        "email",
        "phoneNumber",
        "emailsInCopy",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const { contactPerson, id } = this.state;
    const emailsInCopy = (data.emailsInCopy || [])
      .map((email: string) => email.trim())
      .filter(Boolean);
    const variables = contactPerson
      ? { id: contactPerson.id, ...data, emailsInCopy }
      : { trusteeId: id, ...data, emailsInCopy };

    this.setState({ isSavingContact: true });

    try {
      const response: AxiosResponse<any> = await request({
        query: contactPerson
          ? UPDATE_TRUSTEE_CONTACT_PERSON
          : CREATE_TRUSTEE_CONTACT_PERSON,
        variables,
      });

      const key = contactPerson
        ? "updateTrusteeContactPerson"
        : "createTrusteeContactPerson";
      const payload = response?.data?.data?.[key];

      if (payload?.trusteeContactPerson) {
        message.success("Contact person saved");
        this.setState({ showContactModal: false });
        await this.getTrustee();
      } else {
        message.error(
          payload?.errors?.[0]?.description || "Failed to save contact person",
        );
      }
    } catch (error) {
      message.error(
        `Failed to save contact person - ${generateDisplayErrorMessage(error)}`,
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isSavingContact: false });
    }
  };

  confirmContactDelete = (): void => {
    const { contactPerson, trustee } = this.state;
    if (!contactPerson) return;

    Modal.confirm({
      title: `Remove ${contactPerson.firstName} ${contactPerson.lastName} as the contact for ${trustee?.name}?`,
      content: `This removes the contact from all ${
        contactPerson.activeTrusteeSchemesCount ?? 0
      } schemes under this trustee. Transfer request emails for these schemes will stop until you add a new contact.`,
      okText: "Delete",
      okType: "danger",
      onOk: () => this.deleteContactPerson(contactPerson.id),
    });
  };

  deleteContactPerson = async (contactId: string): Promise<void> => {
    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<any> = await request({
        query: DELETE_TRUSTEE_CONTACT_PERSON,
        variables: { id: contactId },
      });

      const payload = response?.data?.data?.deleteTrusteeContactPerson;

      if (payload?.result) {
        message.success("Contact person deleted");
        await this.getTrustee();
      } else {
        message.error(
          payload?.errors?.[0]?.description ||
            "Failed to delete contact person",
        );
      }
    } catch (error) {
      message.error(
        `Failed to delete contact person - ${generateDisplayErrorMessage(
          error,
        )}`,
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render(): JSX.Element | null {
    const {
      trustee,
      isLoading,
      id,
      showDialog,
      trusteeSchemes,
      name,
      showNewSchemeModal,
      isCreatingScheme,
      contactPerson,
      showContactModal,
      isSavingContact,
    } = this.state;

    return (
      <>
        {showNewSchemeModal &&
          ModalCreator({
            title: "Add Trustee Scheme",
            okType: "primary",
            okText: "Create",
            showModal: showNewSchemeModal,
            onOkay: this.createTrusteeScheme,
            toggleModal: this.toggleCreateSchemeModal,
            inscription: NewEntityUI({
              reference: this.reference,
              isLoading: isCreatingScheme,
              field: "Scheme",
            }),
          })}
        {showContactModal &&
          ModalCreator({
            title: contactPerson ? "Edit Contact Person" : "Add Contact Person",
            okType: "primary",
            okText: "Save",
            showModal: showContactModal,
            onOkay: this.saveContactPerson,
            toggleModal: this.toggleContactModal,
            inscription: (
              <Spin spinning={isSavingContact}>
                <BaseForm
                  size="large"
                  reference={this.contactReference}
                  layout="vertical"
                  fieldsToHide={[]}
                  fields={[
                    {
                      name: "firstName",
                      label: "First Name",
                      inputType: "string",
                      rule: "string",
                      required: true,
                      initialValue: "",
                      colConfig: { span: 11 },
                    },
                    {
                      name: "middleName",
                      label: "Middle Name",
                      inputType: "string",
                      rule: "string",
                      required: false,
                      initialValue: "",
                      colConfig: { span: 11, offset: 1 },
                    },
                    {
                      name: "lastName",
                      label: "Last Name",
                      inputType: "string",
                      rule: "string",
                      required: true,
                      initialValue: "",
                      colConfig: { span: 11 },
                    },
                    {
                      name: "email",
                      label: "Email",
                      inputType: "string",
                      rule: "email",
                      required: true,
                      initialValue: "",
                      colConfig: { span: 11, offset: 1 },
                    },
                    {
                      name: "phoneNumber",
                      label: "Phone",
                      inputType: "string",
                      rule: "string",
                      required: false,
                      initialValue: "",
                      colConfig: { span: 11 },
                    },
                    {
                      name: "emailsInCopy",
                      label: "CC Emails",
                      inputType: "emailArray",
                      rule: "array",
                      required: false,
                      placeholder: "Enter CC email",
                      initialValue: [""],
                      colConfig: { span: 23 },
                      elementStyle: { marginBottom: "8px", width: "100%" },
                    },
                  ]}
                />
              </Spin>
            ),
          })}
        <Spin spinning={isLoading}>
          <Card>
            <Fragment>
              <div>
                {ModalWrapper(
                  showDialog,
                  this.deleteTrustee,
                  this.toggleDeleteConfirmationModal,
                )}
                <Descriptions
                  title={<h2>{trustee?.name}</h2>}
                  column={1}
                  labelStyle={{ fontWeight: "bold" }}
                  extra={
                    <div>
                      <Button
                        className="base-button"
                        size="large"
                        onClick={this.openNotifications}
                        icon={<BellOutlined />}
                        style={{ marginRight: "10px" }}
                        data-cy="test-trustee-notifications-button"
                      >
                        Notifications
                      </Button>
                      <EntitledSection
                        entitlement={entitlements.Trustees.create}
                      >
                        <Button
                          className="base-button"
                          size="large"
                          onClick={this.toggleCreateSchemeModal}
                          icon={<PlusOutlined />}
                          style={{ marginRight: "10px" }}
                          data-cy="test-new-user-button"
                        >
                          Add Scheme
                        </Button>
                      </EntitledSection>
                      <EntitledSection entitlement={entitlements.Trustees.edit}>
                        <NavLink to={`/trustees/${id}/edit?name=${name}`}>
                          <Button
                            icon={<EditOutlined />}
                            size="large"
                            style={{
                              backgroundColor: "  rgb(29, 55, 100)",
                              color: "white",
                              marginRight: "10px",
                            }}
                            data-cy="test-trustee-edit-button"
                          >
                            Edit
                          </Button>
                        </NavLink>
                      </EntitledSection>
                      <EntitledSection
                        entitlement={entitlements.Trustees.delete}
                      >
                        <Button
                          icon={<DeleteOutlined />}
                          size="large"
                          onClick={this.toggleDeleteConfirmationModal}
                          data-cy="test-trustee-delete-button"
                        >
                          Delete
                        </Button>
                      </EntitledSection>
                    </div>
                  }
                >
                  <Descriptions.Item label="Petra ID">
                    {trustee?.company?.petraId || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Postal Address">
                    {trustee?.postalAddress || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Street Name">
                    {trustee?.streetName || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Location">
                    {trustee?.location || "N/A"}
                  </Descriptions.Item>
                  {(trustee?.trusteeCompanySubscriptions || []).map(
                    (cs: TrusteeCompanySubscription) => {
                      const label = trusteeVanLabelFor(cs);
                      return (
                        <Descriptions.Item key={cs.id} label={label}>
                          {cs.companyVan?.van || "N/A"}
                        </Descriptions.Item>
                      );
                    },
                  )}
                </Descriptions>
                <Collapse
                  defaultActiveKey={["contactPerson"]}
                  style={{ marginTop: "20px" }}
                >
                  <Collapse.Panel
                    key="contactPerson"
                    header={<b>Contact Person</b>}
                    extra={
                      // Ant Design toggles the panel on any click within the header, including extra; stop it here so Edit/Delete/Add don't also collapse or expand the panel.
                      <div onClick={(event) => event.stopPropagation()}>
                        {contactPerson ? (
                          <div>
                            <EntitledSection
                              entitlement={entitlements.Trustees.edit}
                            >
                              <Button
                                icon={<EditOutlined />}
                                size="large"
                                style={{ marginRight: "10px" }}
                                onClick={this.openContactModal}
                                data-cy="test-contact-edit-button"
                              >
                                Edit
                              </Button>
                            </EntitledSection>
                            <EntitledSection
                              entitlement={entitlements.Trustees.delete}
                            >
                              <Button
                                icon={<DeleteOutlined />}
                                size="large"
                                onClick={this.confirmContactDelete}
                                data-cy="test-contact-delete-button"
                              >
                                Delete
                              </Button>
                            </EntitledSection>
                          </div>
                        ) : (
                          <EntitledSection
                            entitlement={entitlements.Trustees.edit}
                          >
                            <Button
                              className="base-button"
                              icon={<PlusOutlined />}
                              size="large"
                              onClick={this.openContactModal}
                              data-cy="test-contact-add-button"
                            >
                              Add Contact
                            </Button>
                          </EntitledSection>
                        )}
                      </div>
                    }
                  >
                    <Descriptions
                      column={1}
                      labelStyle={{ fontWeight: "bold" }}
                    >
                      {contactPerson ? (
                        [
                          <Descriptions.Item label="Name" key="name">
                            {[
                              contactPerson.firstName,
                              contactPerson.middleName,
                              contactPerson.lastName,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          </Descriptions.Item>,
                          <Descriptions.Item label="Email" key="email">
                            {contactPerson.email}
                          </Descriptions.Item>,
                          <Descriptions.Item label="Phone" key="phone">
                            {contactPerson.phoneNumber || "N/A"}
                          </Descriptions.Item>,
                          <Descriptions.Item label="CC Emails" key="cc">
                            {contactPerson.emailsInCopy?.length
                              ? contactPerson.emailsInCopy.join(", ")
                              : "N/A"}
                          </Descriptions.Item>,
                          <Descriptions.Item label="Applies to" key="applies">
                            {`Applies to all ${
                              contactPerson.activeTrusteeSchemesCount ?? 0
                            } schemes under this trustee.`}
                          </Descriptions.Item>,
                        ]
                      ) : (
                        <Descriptions.Item key="empty">
                          No contact person set. Transfer request emails for
                          this trustee&apos;s schemes have no recipient.
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Collapse.Panel>
                </Collapse>
              </div>
              {trusteeSchemes && (
                <div>
                  <p
                    style={{
                      marginTop: "20px",
                      fontSize: "15px",
                    }}
                  >
                    <b>Schemes</b>
                  </p>
                  <Table
                    scroll={{ x: 400 }}
                    columns={getSchemeColumns}
                    dataSource={trusteeSchemes}
                  />
                </div>
              )}

              <Drawer
                title="Notification Records"
                width={1300}
                onClose={this.closeNotifications}
                visible={this.state.showNotificationsDrawer}
                bodyStyle={{ paddingBottom: 80 }}
              >
                {NotificationRecordTableFactory(
                  this.state.notifierRecords,
                  notificationColumns,
                  this.state.isLoadingNotifications,
                )}
              </Drawer>
            </Fragment>
          </Card>
        </Spin>
      </>
    );
  }
}
